/**
 * Light/dark theme for Verde Valsesia 2. Light unless the visitor chose dark
 * with the header toggle; the choice lives in localStorage under
 * `THEME_STORAGE_KEY`. The boot script (inlineScript.ts) applies it before
 * first paint, ThemeToggle.tsx changes it.
 *
 * Pure module: no DOM access at module scope and only structural types, so the
 * Node prerender can import it and the tests can pass plain objects.
 */

export const THEME_STORAGE_KEY = 'vv2-theme'

export type Theme = 'light' | 'dark'

/** Browser chrome colour per theme — the `--vv2-paper` canvas of each. */
export const THEME_COLOR: Record<Theme, string> = { light: '#F6F1E7', dark: '#0B1512' }

/** Applies a theme to a root element + optional theme-color meta (structural types, so tests use plain objects). */
export function applyTheme(
  root: { classList: { toggle(c: string, force: boolean): unknown } },
  meta: { setAttribute(n: string, v: string): unknown } | null,
  theme: Theme,
): void {
  root.classList.toggle('dark', theme === 'dark')
  meta?.setAttribute('content', THEME_COLOR[theme])
}

// Change signal for useSyncExternalStore: every mounted ThemeToggle (the bar's
// and the open mobile menu's) re-reads the root class when one of them flips it.
// Only the client subscribes — the server render uses the null snapshot.
const listeners = new Set<() => void>()

/** Adds a theme-change listener; returns the remover. */
export function subscribeTheme(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** Tells every listener the theme changed — call after applyTheme. */
export function notifyTheme(): void {
  for (const cb of listeners) cb()
}
